import { Button, Card, Flex, SimpleTable, Typography } from "djuno-design";
import { useState } from "react";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const FlexPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Flex</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Flex component is a versatile layout container that helps
            structure content using flexbox. It allows for flexible control over
            the direction, alignment of items, and content justification. You
            can also specify different layouts for various screen sizes through
            responsive media queries.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Card component offers various customization options to fit your
            specific needs:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Direction
                </Text>
                : The direction prop determines the orientation of the flex
                container. You can set it to display content in a row, column,
                or reverse order.
              </Text>
              <ul className="marker:text-sky-400 list-disc pl-6">
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Default values
                    </Text>
                    : 'row', 'row-reverse', 'col', 'col-reverse'
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    Supports responsive media query breakpoints: default, sm,
                    md, lg, xl, 2xl
                  </Text>
                </li>
              </ul>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Justification
                </Text>
                : The justify prop controls how space is distributed between
                items in the flex container
              </Text>
              <ul className="marker:text-sky-400 list-disc pl-6">
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Available values
                    </Text>
                    : 'start', 'end', 'center', 'between', 'around', 'evenly',
                    'stretch'
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    Also supports breakpoints for responsive design.
                  </Text>
                </li>
              </ul>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Alignment (Items)
                </Text>
                : The items prop aligns flex items along the cross-axis
              </Text>
              <ul className="marker:text-sky-400 list-disc pl-6">
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Available values
                    </Text>
                    : 'start', 'end', 'center', 'baseline', 'stretch'
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    Can be adjusted per breakpoint for responsive layouts
                  </Text>
                </li>
              </ul>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>

          <ExpandableCard
            description="Flex"
            codeViewerProps={{
              code: FlexExample1,
            }}
          >
            <Flex direction="col" className="gap-2 dj-w-full">
              <Flex items="center" className="gap-2 dj-w-full">
                <Button>1</Button>
                <Button>2</Button>
                <Button>3</Button>
              </Flex>
              <Flex justify="center" items="center" className="gap-2 dj-w-full">
                <Button>1</Button>
                <Button>2</Button>
                <Button>3</Button>
              </Flex>
              <Flex justify="end" items="center" className="gap-2 dj-w-full">
                <Button>1</Button>
                <Button>2</Button>
                <Button>3</Button>
              </Flex>
              <Flex
                direction={{ default: "row", md: "col" }}
                items={{ lg: "center" }}
                className="gap-2 dj-w-full"
              >
                <Button>1</Button>
                <Button>2</Button>
                <Button>3</Button>
              </Flex>
            </Flex>
          </ExpandableCard>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* direction */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">direction</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the flex direction of the container. Supports
                        responsive breakpoints.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        href={GithubTypesLink + "Flex.ts#L24"}
                        target="_blank"
                        size="xs"
                        code
                      >
                        {`FlexDirection | IMediaQuery<FlexDirection>`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">row</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* items */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">items</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Specifies the alignment of flex items. Supports
                        responsive breakpoints.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Flex.ts#L25"}
                        size="xs"
                        code
                      >
                        {`FlexItems | IMediaQuery<FlexItems>`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">start</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* justify */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">justify</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Determines how the items should be justified within the
                        flex container.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Flex.ts#L26"}
                        size="xs"
                        code
                      >{`FlexJustify | IMediaQuery<FlexJustify>`}</Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">start</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content to be laid out flexibly.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>{`React.ReactNode`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* className */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">className</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional class names to customize the card's
                        appearance.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const FlexExample1 = `<Flex direction="col" className="gap-2 dj-w-full">
    <Flex items="center" className="gap-2 dj-w-full">
      <Button>1</Button>
      <Button>2</Button>
      <Button>3</Button>
    </Flex>
    <Flex justify="center" items="center" className="gap-2 dj-w-full">
      <Button>1</Button>
      <Button>2</Button>
      <Button>3</Button>
    </Flex>
    <Flex justify="end" items="center" className="gap-2 dj-w-full">
      <Button>1</Button>
      <Button>2</Button>
      <Button>3</Button>
    </Flex>
    <Flex                 
      direction={{ default: "row", md: "col" }}
      items={{ lg: "center" }} className="gap-2 dj-w-full"
    >
        <Button>1</Button>
        <Button>2</Button>
        <Button>3</Button>
    </Flex>
  </Flex>`;

export default FlexPage;
